<template>
  <div id="client-events" class="ce-root">
    <div v-if="loaded">

      <!-- Header -->
      <div class="ce-header">
        <div class="ce-title">
          <i class="fa-solid fa-calendar-days ce-title-icon"></i>
          <span>{{ $t('tabs.clients.events') }}</span>
          <span class="ce-count" v-if="events.length">{{ filtered.length }}/{{ events.length }}</span>
        </div>
        <div class="ce-header-actions">
          <b-spinner small v-if="loading"/>
          <button v-else class="ce-icon-btn" :title="$t('operations.refresh')" @click="loadData">
            <i class="fa-solid fa-rotate-right"></i>
          </button>
          <b-button size="sm" variant="primary" class="ce-add-btn" @click="openModal()">
            <i class="fa-solid fa-plus mr-1"></i>{{ $t('operations.addNewEvent') }}
          </b-button>
        </div>
      </div>

      <!-- Toolbar: search + filter chips (match notes layout for height parity) -->
      <div class="ce-toolbar">
        <b-input-group size="sm" class="ce-search">
          <b-input-group-prepend>
            <b-input-group-text><i class="fa-solid fa-magnifying-glass"></i></b-input-group-text>
          </b-input-group-prepend>
          <b-form-input v-model="search" :placeholder="$t('operations.search')"/>
        </b-input-group>
        <div class="ce-chips">
          <button class="ce-chip" :class="{ active: filter === 'all' }" @click="filter = 'all'">
            {{ $t('all') }}
          </button>
          <button class="ce-chip" :class="{ active: filter === 'upcoming' }" @click="filter = 'upcoming'">
            <i class="fa-regular fa-clock"></i> {{ $t('upcoming') || 'Upcoming' }}
          </button>
          <button class="ce-chip" :class="{ active: filter === 'past' }" @click="filter = 'past'">
            <i class="fa-regular fa-circle-check"></i> {{ $t('past') || 'Past' }}
          </button>
        </div>
      </div>

      <!-- Timeline list -->
      <div class="ce-list" v-if="filtered.length > 0">
        <div v-for="ev in filtered" :key="ev.id"
             class="ce-event"
             :class="{ 'ce-past': isPast(ev), 'ce-upcoming': !isPast(ev) }"
        >
          <div class="ce-event-marker">
            <i :class="['fa-solid', typeIcon(ev.type)]"></i>
          </div>
          <div class="ce-event-card">
            <div class="ce-event-header">
              <div class="ce-event-meta">
                <span class="ce-type-badge" :class="`ce-type-${(ev.type || 'OTHER').toLowerCase()}`">
                  {{ typeLabel(ev.type) }}
                </span>
                <span class="ce-event-time">
                  <i class="fa-regular fa-clock"></i>
                  {{ ev.start | moment($t('timeFormat.dateTime.format')) }}
                </span>
                <span class="ce-event-duration" v-if="ev.duration">
                  <i class="fa-regular fa-hourglass-half"></i> {{ formatDuration(ev.duration) }}
                </span>
                <span v-if="!isPast(ev)" class="ce-flag ce-flag-upcoming">{{ $t('upcoming') || 'Upcoming' }}</span>
                <span v-else class="ce-flag ce-flag-past">{{ $t('past') || 'Past' }}</span>
              </div>
              <div class="ce-event-actions">
                <button class="ce-icon-btn" :title="$t('operations.edit')" @click="onEditEvent(ev)">
                  <i class="fa fa-edit"></i>
                </button>
                <button class="ce-icon-btn ce-icon-danger" :title="$t('operations.delete')" @click="onDeleteEvent(ev.id)">
                  <i class="fa fa-trash"></i>
                </button>
              </div>
            </div>
            <div class="ce-event-body" v-if="ev.body">{{ ev.body }}</div>
            <div class="ce-event-body ce-event-empty-body" v-else>—</div>
          </div>
        </div>
      </div>

      <!-- Empty -->
      <div v-else class="ce-empty">
        <i class="fa-regular fa-calendar ce-empty-icon"></i>
        <div class="ce-empty-text">
          {{ search || filter !== 'all' ? $t('noResults') : $t('noEventsYet') }}
        </div>
        <b-button v-if="!search && filter === 'all'" size="sm" variant="outline-primary" class="mt-2" @click="openModal()">
          <i class="fa-solid fa-plus mr-1"></i>{{ $t('operations.addNewEvent') }}
        </b-button>
      </div>

      <!-- New event modal -->
      <b-modal v-model="showModal" no-close-on-backdrop hide-footer hide-header centered
               dialog-class="ce-modal-dialog" body-class="ce-modal-body">
        <div class="ce-modal-header">
          <div class="ce-modal-icon"><i class="fa-solid fa-calendar-plus"></i></div>
          <div class="ce-modal-titles">
            <div class="ce-modal-title">{{ $t('operations.newEvent') }}</div>
            <div class="ce-modal-subtitle">{{ $t('columns.event') }}</div>
          </div>
          <button class="ce-modal-close" @click="showModal = false"><i class="fa-solid fa-xmark"></i></button>
        </div>
        <b-form @submit.prevent="save" class="ce-modal-form">
          <div class="ce-modal-row">
            <label class="ce-modal-label">{{ $t('columns.type') }}</label>
            <v-select v-model="newEvent.type" :options="types" :clearable="false" label="label" :reduce="(o) => o.name"/>
          </div>
          <div class="ce-modal-row">
            <label class="ce-modal-label">{{ $t('columns.time') }}</label>
            <date-time-picker v-model="newEvent.start"/>
          </div>
          <div class="ce-modal-row">
            <label class="ce-modal-label">{{ $t('columns.duration') }}</label>
            <v-select v-model="newEvent.duration" :options="durationOptions" :clearable="false" label="text" :reduce="(o) => o.value"/>
          </div>
          <div class="ce-modal-row">
            <label class="ce-modal-label">{{ $t('columns.comment') }}</label>
            <b-form-textarea v-model="newEvent.body" rows="4" :placeholder="$t('writeCommentHere')" class="ce-modal-textarea"/>
          </div>
          <div class="ce-modal-footer">
            <b-button variant="link" class="ce-modal-cancel" @click="showModal = false">{{ $t('operations.cancel') }}</b-button>
            <b-button type="submit" variant="primary" class="ce-modal-submit">
              <i class="fa-solid fa-floppy-disk mr-1"></i>{{ $t('operations.save') }}
            </b-button>
          </div>
        </b-form>
      </b-modal>

      <!-- Edit event modal -->
      <b-modal v-model="showEditModal" no-close-on-backdrop hide-footer hide-header centered
               dialog-class="ce-modal-dialog" body-class="ce-modal-body">
        <div class="ce-modal-header">
          <div class="ce-modal-icon"><i class="fa-solid fa-pen-to-square"></i></div>
          <div class="ce-modal-titles">
            <div class="ce-modal-title">{{ $t('operations.editEvent') }}</div>
            <div class="ce-modal-subtitle">{{ $t('columns.event') }}</div>
          </div>
          <button class="ce-modal-close" @click="showEditModal = false"><i class="fa-solid fa-xmark"></i></button>
        </div>
        <b-form @submit.prevent="saveEdit" class="ce-modal-form">
          <div class="ce-modal-row">
            <label class="ce-modal-label">{{ $t('columns.type') }}</label>
            <v-select v-model="editEvent.type" :options="types" :clearable="false" label="label" :reduce="(o) => o.name"/>
          </div>
          <div class="ce-modal-row">
            <label class="ce-modal-label">{{ $t('columns.time') }}</label>
            <date-time-picker v-model="editEvent.start"/>
          </div>
          <div class="ce-modal-row">
            <label class="ce-modal-label">{{ $t('columns.duration') }}</label>
            <v-select v-model="editEvent.duration" :options="durationOptions" :clearable="false" label="text" :reduce="(o) => o.value"/>
          </div>
          <div class="ce-modal-row">
            <label class="ce-modal-label">{{ $t('columns.comment') }}</label>
            <b-form-textarea v-model="editEvent.body" rows="4" :placeholder="$t('writeCommentHere')" class="ce-modal-textarea"/>
          </div>
          <div class="ce-modal-footer">
            <b-button variant="link" class="ce-modal-cancel" @click="showEditModal = false">{{ $t('operations.cancel') }}</b-button>
            <b-button type="submit" variant="primary" class="ce-modal-submit">
              <i class="fa-solid fa-floppy-disk mr-1"></i>{{ $t('operations.save') }}
            </b-button>
          </div>
        </b-form>
      </b-modal>

    </div>

    <div v-else-if="error !== ''">
      <b-alert show variant="danger">
        <i class="fas fa-exclamation-triangle mr-2"></i><strong>{{ error }}</strong>
      </b-alert>
    </div>

    <div v-else class="ce-loading">
      <b-spinner label="Spinning"/>
      <div>{{ $t('operations.loading') }}</div>
    </div>
  </div>
</template>

<script>
import { mapActions } from 'vuex';
import mixinBase from '@/components/mixin/mixinBase';
import mixinNotifications from '@/components/mixin/mixinNotifications';
import mixinPermissions from '@/components/mixin/mixinPermissions';
import mixinValidation from '@/components/mixin/mixinValidation';
import DateTimePicker from '@/components/widget/picker/dateTimePicker.vue'

export default {
  name: 'ClientEvents',
  components: { DateTimePicker },
  props: {
    autoload: { default: false, type: Boolean },
    clientId: String,
    // The client's org — required by the backend when the current user
    // belongs to more than one organization (it cannot auto-pick one).
    orgId: { default: null, type: String },
  },
  mixins: [mixinBase, mixinNotifications, mixinPermissions, mixinValidation],
  data() {
    return {
      loaded: false,
      loading: false,
      durationOptions: [
        { value: 5, text: '5 min' },
        { value: 10, text: '10 min' },
        { value: 15, text: '15 min' },
        { value: 30, text: '30 min' },
        { value: 45, text: '45 min' },
        { value: 60, text: '1h' },
        { value: 120, text: '2h' },
        { value: 180, text: '3h' },
        { value: 360, text: '6h' }
      ],
      error: '',
      showModal: false,
      showEditModal: false,
      editEvent: { id: null, start: null, type: null, duration: null, body: '' },
      newEvent: this.defaultNewEvent(),
      events: [],
      search: '',
      filter: 'all',
    };
  },
  watch: {
    clientId() {
      this.reset();
      if (this.autoload) {
        this.loadData();
      }
    }
  },
  created() {
    this.loadData();
  },
  computed: {
    types() {
      let types = this.$t('calendarTypes');
      return Object.keys(types).map((key) => ({
        name: key,
        label: this.$t(`calendarTypes.${key}.name`)
      }));
    },
    eventsSorted() {
      return [...this.events].sort((a, b) => {
        const ta = a.start ? new Date(a.start).getTime() : 0;
        const tb = b.start ? new Date(b.start).getTime() : 0;
        return tb - ta;
      });
    },
    filtered() {
      const q = (this.search || '').trim().toLowerCase();
      return this.eventsSorted.filter(ev => {
        if (this.filter === 'past' && !this.isPast(ev)) return false;
        if (this.filter === 'upcoming' && this.isPast(ev)) return false;
        if (q) {
          const txt = ((ev.body || '') + ' ' + (this.typeLabel(ev.type) || '')).toLowerCase();
          if (!txt.includes(q)) return false;
        }
        return true;
      });
    }
  },
  methods: {
    ...mapActions('calendar', ['addCalendar', 'cancelEvent', 'updateCalendar', 'getEventsByClient']),

    isPast(ev) {
      if (!ev || !ev.start) return false;
      const end = ev.duration
          ? new Date(new Date(ev.start).getTime() + ev.duration * 60000)
          : new Date(ev.start);
      return end.getTime() < Date.now();
    },
    typeLabel(type) {
      if (!type) return '';
      const key = `calendarTypes.${type}.name`;
      return this.$te(key) ? this.$t(key) : type;
    },
    typeIcon(type) {
      switch ((type || '').toUpperCase()) {
        case 'CALL': return 'fa-phone';
        case 'EMAIL': return 'fa-envelope';
        case 'MEETING': return 'fa-handshake';
        case 'TASK': return 'fa-list-check';
        case 'FOLLOW_UP':
        case 'FOLLOWUP': return 'fa-rotate-right';
        default: return 'fa-calendar-day';
      }
    },
    onEditEvent(item) {
      this.editEvent = {
        id: item.id,
        start: item.start,
        type: item.type || null,
        duration: item.duration || null,
        body: item.body || '',
      };
      this.showEditModal = true;
    },
    saveEdit() {
      try {
        // Same guard as save(): a cleared date would serialize "Invalid date".
        if (!this.editEvent.start || !this.$moment(this.editEvent.start).isValid()) {
          this.showErrorNotification(this.$t('globalMessages.updateDataError', { system: this.$t('event') }), this.$t('eventTimeRequired'));
          return;
        }
        this.editEvent.end = this.$moment(this.editEvent.start)
            .add(this.editEvent.duration, 'minutes')
            .toServerDateTimeFormat();
        this.updateCalendar({ calendar: this.editEvent, id: this.editEvent.id })
            .then(() => {
              this.loadData();
              this.showSuccessNotification(this.$t('globalMessages.updateDataSuccess', { system: this.$t('event') }));
              this.showEditModal = false;
            })
            .catch((e) => {
              this.error = e.toString();
              this.showErrorNotification(
                  this.$t('globalMessages.updateDataError', { system: this.$t('event') }), 'Error updating event');
            });
      } catch (err) {
        console.error('Error saving event:', err);
        this.showErrorNotification('Unexpected error while saving event');
      }
    },
    formatDuration(minutes) {
      if (minutes < 60) return `${minutes}m`;
      const hours = Math.floor(minutes / 60);
      const remaining = minutes % 60;
      return `${hours}h${remaining > 0 ? ` ${remaining}m` : ''}`;
    },
    defaultNewEvent() {
      // start defaults to NOW (UTC server format — same shape the picker
      // emits): the picker used to only DISPLAY "now" while the actual value
      // stayed null, which is what produced the "Invalid date" saves.
      return { clientIds: [], start: this.$moment().utc().toServerDateTimeFormat(), duration: null, type: 'CALL', body: '' };
    },
    openModal() {
      this.newEvent = this.defaultNewEvent();
      this.showModal = true;
    },
    reset() {
      this.loaded = false;
      this.error = '';
      this.newEvent = this.defaultNewEvent();
      this.events = [];
    },
    loadData() {
      if (this.loading) return;
      this.loading = true;
      this.getEventsByClient(this.clientId)
          .then(eventData => {
            this.events = eventData;
            this.loaded = true;
          })
          .catch(e => {
            this.error = e.toString();
          })
          .finally(() => {
            this.loading = false;
          });
    },
    onDeleteEvent(id) {
      this.cancelEvent(id)
          .then(() => {
            this.loadData();
            this.showSuccessNotification(this.$t('globalMessages.deleteDataSuccess', { system: this.$t('event') }));
          })
          .catch(e => {
            this.error = e.toString();
            this.showErrorNotification(this.$t('globalMessages.deleteDataError', { system: this.$t('event') }), 'Error deleting event');
          });
    },
    save() {
      let newEvent = { ...this.newEvent };
      // No start picked → moment(null) formats to the literal "Invalid date"
      // string, which the backend cannot deserialize into a LocalDateTime.
      if (!newEvent.start || !this.$moment(newEvent.start).isValid()) {
        this.showErrorNotification(this.$t('globalMessages.createDataError', { system: this.$t('event') }), this.$t('eventTimeRequired'));
        return;
      }
      newEvent.end = this.$moment(newEvent.start).add(newEvent.duration, 'minutes').toServerDateTimeFormat();
      newEvent.clientsIds = [this.clientId];
      if (this.orgId) {
        newEvent.orgId = this.orgId;
      }
      this.addCalendar(newEvent)
          .then(() => {
            this.reset();
            this.loadData();
            this.showModal = false;
            this.showSuccessNotification(this.$t('globalMessages.createDataSuccess', { system: this.$t('event') }));
          })
          .catch(e => {
            this.error = e.toString();
            this.showErrorNotification(this.$t('globalMessages.createDataError', { system: this.$t('event') }),
                this.getErrorDescription(e.response ? e.response.data : e));
          });
    },
  }
};
</script>

<style scoped>
.ce-root {
  min-height: 220px;
  padding: 4px 2px;
}

/* ====== HEADER ====== */
.ce-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 6px 4px 12px;
  flex-wrap: wrap;
  gap: 8px;
}

.ce-title {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 1rem;
  font-weight: 700;
  letter-spacing: 0.4px;
  color: #2d3142;
  text-transform: uppercase;
}

.ce-title-icon {
  color: #007bff;
  font-size: 1.05rem;
}

.ce-count {
  font-size: 0.7rem;
  font-weight: 700;
  background: #007bff;
  color: #fff;
  padding: 2px 9px;
  border-radius: 999px;
}

.ce-header-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.ce-add-btn {
  border-radius: 8px;
  font-weight: 600;
}

/* ====== TOOLBAR (matches notes) ====== */
.ce-toolbar {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  padding: 4px 4px 14px;
  align-items: center;
  border-bottom: 1px solid #e6ebf2;
  margin-bottom: 12px;
}

.ce-search { flex: 1 1 220px; max-width: 360px; }
.ce-search >>> .input-group-text {
  background: #f4f7fb;
  border-color: #d8dde6;
  color: #6c757d;
}
.ce-search >>> .form-control { border-color: #d8dde6; }

.ce-chips { display: flex; gap: 6px; flex-wrap: wrap; }

.ce-chip {
  background: #ffffff;
  border: 1px solid #d8dde6;
  border-radius: 999px;
  padding: 5px 14px;
  font-size: 0.78rem;
  font-weight: 600;
  color: #4a5365;
  cursor: pointer;
  transition: all 0.15s ease;
  display: inline-flex;
  align-items: center;
  gap: 5px;
}
.ce-chip:hover { background: #f0f7ff; color: #007bff; border-color: #b6d6ff; }
.ce-chip.active {
  background: #007bff;
  color: #fff;
  border-color: #007bff;
  box-shadow: 0 2px 4px rgba(0, 123, 255, 0.25);
}

.dark-layout .ce-toolbar { border-bottom-color: #3d4452; }
.dark-layout .ce-search >>> .input-group-text {
  background: #1f242e; border-color: #3d4452; color: #98a0b3;
}
.dark-layout .ce-search >>> .form-control {
  background: #2a2f3a; border-color: #3d4452; color: #e2e6ee;
}
.dark-layout .ce-chip {
  background: #2a2f3a; border-color: #3d4452; color: #b8c0d0;
}
.dark-layout .ce-chip:hover {
  background: rgba(0, 123, 255, 0.18); color: #6ab0ff; border-color: #4d80b8;
}

/* ====== LIST (timeline) ====== */
.ce-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 0 2px;
  position: relative;
}

.ce-event {
  display: flex;
  gap: 12px;
  align-items: flex-start;
}

.ce-event-marker {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: linear-gradient(135deg, #6da7ff 0%, #007bff 100%);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.85rem;
  flex-shrink: 0;
  box-shadow: 0 2px 6px rgba(0, 123, 255, 0.25);
  margin-top: 4px;
}

.ce-event.ce-past .ce-event-marker {
  background: #b9c2d0;
  box-shadow: none;
  opacity: 0.85;
}

.ce-event-card {
  flex: 1;
  background: #ffffff;
  border: 1px solid #d8dde6;
  border-radius: 10px;
  padding: 12px 14px;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
  transition: box-shadow 0.18s ease, border-color 0.18s ease;
  min-width: 0;
}

.ce-event-card:hover {
  border-color: #b9c2d0;
  box-shadow: 0 2px 10px rgba(15, 23, 42, 0.08);
}

.ce-event.ce-past .ce-event-card {
  background: #f8f9fb;
}

.ce-event-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 10px;
  margin-bottom: 8px;
}

.ce-event-meta {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  font-size: 0.78rem;
  color: #4a5365;
}

.ce-type-badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background: #007bff;
  color: #fff;
  font-size: 0.7rem;
  font-weight: 700;
  padding: 3px 10px;
  border-radius: 999px;
  letter-spacing: 0.3px;
  text-transform: uppercase;
}

.ce-type-call { background: #28a745; }
.ce-type-email { background: #6f42c1; }
.ce-type-meeting { background: #fd7e14; }
.ce-type-task { background: #17a2b8; }
.ce-type-follow_up,
.ce-type-followup { background: #ffc107; color: #4a3500; }

.ce-event-time,
.ce-event-duration {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-variant-numeric: tabular-nums;
}

.ce-flag {
  display: inline-flex;
  align-items: center;
  font-size: 0.66rem;
  font-weight: 700;
  padding: 2px 8px;
  border-radius: 999px;
  letter-spacing: 0.3px;
  text-transform: uppercase;
}

.ce-flag-upcoming {
  background: #e6f7ec;
  color: #1f7a3a;
}

.ce-flag-past {
  background: #eef2f7;
  color: #6c757d;
}

.ce-event-actions {
  display: flex;
  gap: 4px;
  flex-shrink: 0;
}

.ce-icon-btn {
  background: #ffffff;
  border: 1px solid #d8dde6;
  color: #4a5365;
  width: 30px;
  height: 30px;
  border-radius: 7px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.15s ease;
  font-size: 0.8rem;
}

.ce-icon-btn:hover {
  background: #f0f7ff;
  color: #007bff;
  border-color: #b6d6ff;
}

.ce-icon-btn.ce-icon-danger:hover {
  background: #fde8ea;
  color: #dc3545;
  border-color: #f5b8be;
}

.ce-event-body {
  font-size: 0.92rem;
  line-height: 1.5;
  color: #2d3142;
  white-space: pre-wrap;
  word-break: break-word;
}

.ce-event-empty-body {
  color: #b9c2d0;
  font-style: italic;
}

/* ====== EMPTY ====== */
.ce-empty {
  text-align: center;
  padding: 36px 20px;
  color: #4a5365;
  background: #f4f7fb;
  border: 1px dashed #d8dde6;
  border-radius: 10px;
}

.ce-empty-icon {
  font-size: 2.6rem;
  color: #b9c2d0;
  margin-bottom: 12px;
  display: block;
}

.ce-empty-text {
  font-size: 0.95rem;
  font-weight: 500;
}

.ce-loading {
  text-align: center;
  padding: 30% 20px 20px;
}

/* ====== DARK MODE ====== */
.dark-layout .ce-title {
  color: #e2e6ee;
}

.dark-layout .ce-title-icon {
  color: #6ab0ff;
}

.dark-layout .ce-event-card {
  background: #2a2f3a;
  border-color: #3d4452;
}

.dark-layout .ce-event-card:hover {
  border-color: #4d556a;
}

.dark-layout .ce-event.ce-past .ce-event-card {
  background: #232831;
}

.dark-layout .ce-event-meta {
  color: #b8c0d0;
}

.dark-layout .ce-event-body {
  color: #e2e6ee;
}

.dark-layout .ce-event-empty-body {
  color: #4d556a;
}

.dark-layout .ce-flag-upcoming {
  background: rgba(31, 122, 58, 0.2);
  color: #6dd29a;
}

.dark-layout .ce-flag-past {
  background: rgba(255, 255, 255, 0.05);
  color: #98a0b3;
}

.dark-layout .ce-icon-btn {
  background: #1f242e;
  border-color: #3d4452;
  color: #b8c0d0;
}

.dark-layout .ce-icon-btn:hover {
  background: rgba(0, 123, 255, 0.18);
  color: #6ab0ff;
  border-color: #4d80b8;
}

.dark-layout .ce-empty {
  background: #1f242e;
  border-color: #3d4452;
  color: #b8c0d0;
}

.dark-layout .ce-empty-icon {
  color: #4d556a;
}

.dark-layout .ce-event.ce-past .ce-event-marker {
  background: #4d556a;
}
</style>

<style>
/* Event modal — global because dialog-class lives outside scoped subtree */
.ce-modal-dialog .modal-content {
  border-radius: 16px;
  border: none;
  box-shadow: 0 24px 48px rgba(15, 23, 42, 0.25), 0 8px 16px rgba(15, 23, 42, 0.12);
  overflow: hidden;
}
.ce-modal-body {
  padding: 0 !important;
  background: #ffffff;
}
.dark-layout .ce-modal-body { background: #2a2f3a; }

.ce-modal-header {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 18px 22px;
  background: linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%);
  border-bottom: 1px solid #bfdbfe;
}
.dark-layout .ce-modal-header {
  background: linear-gradient(135deg, rgba(96, 165, 250, 0.15) 0%, rgba(96, 165, 250, 0.05) 100%);
  border-bottom-color: rgba(96, 165, 250, 0.25);
}

.ce-modal-icon {
  width: 44px; height: 44px;
  border-radius: 12px;
  background: linear-gradient(135deg, #60a5fa 0%, #2563eb 100%);
  color: #fff;
  display: flex; align-items: center; justify-content: center;
  font-size: 1.1rem;
  box-shadow: 0 3px 10px rgba(37, 99, 235, 0.35);
  flex-shrink: 0;
}

.ce-modal-titles { flex: 1; min-width: 0; }
.ce-modal-title {
  font-size: 1.05rem;
  font-weight: 700;
  color: #0f172a;
  letter-spacing: -0.2px;
}
.ce-modal-subtitle {
  font-size: 0.74rem;
  font-weight: 600;
  color: #1d4ed8;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  margin-top: 2px;
}
.dark-layout .ce-modal-title { color: #f1f5f9; }
.dark-layout .ce-modal-subtitle { color: #93c5fd; }

.ce-modal-close {
  width: 32px; height: 32px;
  border-radius: 8px;
  border: none;
  background: rgba(15, 23, 42, 0.05);
  color: #475569;
  display: flex; align-items: center; justify-content: center;
  cursor: pointer;
  font-size: 0.9rem;
  transition: all 0.15s ease;
}
.ce-modal-close:hover {
  background: rgba(220, 38, 38, 0.1);
  color: #dc2626;
}
.dark-layout .ce-modal-close {
  background: rgba(255, 255, 255, 0.06);
  color: #cbd5e1;
}

.ce-modal-form { padding: 18px 22px 20px; }

.ce-modal-row {
  margin-bottom: 14px;
}

.ce-modal-label {
  display: block;
  font-size: 0.74rem;
  font-weight: 700;
  color: #475569;
  letter-spacing: 0.4px;
  text-transform: uppercase;
  margin-bottom: 6px;
}
.dark-layout .ce-modal-label { color: #94a3b8; }

.ce-modal-textarea {
  border-radius: 10px;
  padding: 10px 12px;
  font-size: 0.92rem;
  line-height: 1.5;
  resize: vertical;
}

.ce-modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 20px;
  padding-top: 16px;
  border-top: 1px solid #e2e8f0;
}
.dark-layout .ce-modal-footer { border-top-color: #475569; }

.ce-modal-cancel {
  color: #475569;
  font-weight: 500;
  text-decoration: none;
}
.ce-modal-cancel:hover { color: #0f172a; }
.dark-layout .ce-modal-cancel { color: #94a3b8; }
.dark-layout .ce-modal-cancel:hover { color: #f1f5f9; }

.ce-modal-submit {
  border-radius: 9px;
  padding: 8px 22px;
  font-weight: 600;
  box-shadow: 0 2px 8px rgba(37, 99, 235, 0.25);
}

/* ====== FORM CONTROLS — match the new client-details spec-sheet inputs ======
   Selectors are anchored on .ce-modal-dialog to out-specify the Vuexy theme's
   global .dark-layout .form-control rules (which otherwise override these). */
.ce-modal-dialog .modal-content {
  --cem-input-bg: #ffffff;
  --cem-input-border: #cbd5e1;
  --cem-input-border-strong: #94a3b8;
  --cem-input-text: #0f172a;
  --cem-primary: #2563eb;
  --cem-primary-soft: #eff6ff;
}
.dark-layout .ce-modal-dialog .modal-content {
  --cem-input-bg: rgba(255, 255, 255, 0.045);
  --cem-input-border: rgba(255, 255, 255, 0.18);
  --cem-input-border-strong: rgba(255, 255, 255, 0.32);
  --cem-input-text: #f1f5f9;
  --cem-primary: #93caff;
  --cem-primary-soft: rgba(147, 202, 255, 0.22);
}

/* Text inputs, datepicker field, textarea */
.ce-modal-dialog .ce-modal-form .form-control,
.ce-modal-dialog .ce-modal-form .ce-modal-textarea,
.dark-layout .ce-modal-dialog .ce-modal-form .form-control,
.dark-layout .ce-modal-dialog .ce-modal-form .ce-modal-textarea {
  background-color: var(--cem-input-bg);
  border: 1px solid var(--cem-input-border);
  border-radius: 8px;
  color: var(--cem-input-text);
  font-size: 0.9rem;
  box-shadow: none;
  transition: border-color 0.15s ease, box-shadow 0.15s ease, background-color 0.15s ease;
}
.ce-modal-dialog .ce-modal-form .form-control:hover,
.dark-layout .ce-modal-dialog .ce-modal-form .form-control:hover {
  border-color: var(--cem-input-border-strong);
}
.ce-modal-dialog .ce-modal-form .form-control:focus,
.ce-modal-dialog .ce-modal-form .ce-modal-textarea:focus,
.dark-layout .ce-modal-dialog .ce-modal-form .form-control:focus,
.dark-layout .ce-modal-dialog .ce-modal-form .ce-modal-textarea:focus {
  background-color: var(--cem-input-bg);
  border-color: var(--cem-primary);
  box-shadow: 0 0 0 3px var(--cem-primary-soft);
  outline: none;
}
.ce-modal-dialog .ce-modal-form .form-control::placeholder {
  color: var(--cem-input-border-strong);
  opacity: 1;
}

/* Date-time picker: tighten lg sizing + theme the calendar append */
.ce-modal-dialog .ce-modal-form .input-group .form-control {
  height: 38px;
}
.ce-modal-dialog .ce-modal-form .input-group-text,
.dark-layout .ce-modal-dialog .ce-modal-form .input-group-text {
  background: var(--cem-input-bg);
  border: 1px solid var(--cem-input-border);
  border-left: none;
  border-radius: 0 8px 8px 0;
  color: var(--cem-input-border-strong);
  cursor: pointer;
  transition: color 0.15s ease;
}
.ce-modal-dialog .ce-modal-form .input-group-text:hover {
  color: var(--cem-primary);
}
.ce-modal-dialog .ce-modal-form .input-group .form-control:not(:last-child) {
  border-right: none;
  border-radius: 8px 0 0 8px;
}

/* v-select (type / duration) */
.ce-modal-dialog .ce-modal-form .v-select .vs__dropdown-toggle,
.dark-layout .ce-modal-dialog .ce-modal-form .v-select .vs__dropdown-toggle {
  background: var(--cem-input-bg);
  border: 1px solid var(--cem-input-border);
  border-radius: 8px;
  min-height: 38px;
  padding: 2px 6px;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}
.ce-modal-dialog .ce-modal-form .v-select:hover .vs__dropdown-toggle {
  border-color: var(--cem-input-border-strong);
}
.ce-modal-dialog .ce-modal-form .v-select.vs--open .vs__dropdown-toggle,
.ce-modal-dialog .ce-modal-form .v-select:focus-within .vs__dropdown-toggle {
  border-color: var(--cem-primary);
  box-shadow: 0 0 0 3px var(--cem-primary-soft);
}
.ce-modal-dialog .ce-modal-form .v-select .vs__selected,
.ce-modal-dialog .ce-modal-form .v-select .vs__search,
.dark-layout .ce-modal-dialog .ce-modal-form .v-select .vs__selected,
.dark-layout .ce-modal-dialog .ce-modal-form .v-select .vs__search {
  color: var(--cem-input-text);
  font-size: 0.9rem;
}
.dark-layout .ce-modal-dialog .ce-modal-form .v-select .vs__open-indicator,
.dark-layout .ce-modal-dialog .ce-modal-form .v-select .vs__clear {
  fill: #94a3b8;
}
.dark-layout .ce-modal-dialog .ce-modal-form .v-select .vs__dropdown-menu {
  background: #2a2f3a;
  border-color: #3d4452;
}
.dark-layout .ce-modal-dialog .ce-modal-form .v-select .vs__dropdown-option {
  color: #e2e6ee;
}
.dark-layout .ce-modal-dialog .ce-modal-form .v-select .vs__dropdown-option--highlight {
  background: rgba(96, 165, 250, 0.20);
  color: #fff;
}

/* Calendar/time popover in dark mode (date-time picker) */
.dark-layout .date-time-picker-popover .popover-body {
  background: #2a2f3a;
}
.dark-layout .date-time-picker-popover.popover {
  background: #2a2f3a;
  border-color: #3d4452;
}
</style>
